/**
 * ToolForm.jsx
 * ============
 * Inline form rendered in the chat panel when a tool has required inputs.
 * The user fills in the fields and clicks "Send to Tegan"; the component
 * assembles a natural-language command string and calls onSubmit(cmd).
 *
 * Props
 * -----
 *   tool     – ToolManifest (must have .inputs array)
 *   onSubmit – (commandString: string) => void
 *   onCancel – () => void
 */

import { useState } from 'react';
import * as LucideIcons from 'lucide-react';

/** Convert kebab-case icon name → PascalCase → Lucide component, fallback Zap. */
function getIcon(iconName, size = 16) {
  if (!iconName) return <LucideIcons.Zap size={size} />;
  const pascal = iconName
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
  const Icon = LucideIcons[pascal];
  return Icon ? <Icon size={size} /> : <LucideIcons.Zap size={size} />;
}

/** Humanise tool.name when display_name is absent. */
function humanise(name = '') {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Build the command string sent to Tegan.
 * Tool-specific patterns first; default fallback joins all values.
 */
function buildCommand(tool, values) {
  const label = (tool.display_name || humanise(tool.name)).toLowerCase();

  if (label === 'send email') {
    return `send email to ${values.to || ''} subject ${values.subject || ''} body ${values.body || ''}`.trim();
  }
  if (label === 'check calendar') {
    return values.date
      ? `check calendar for ${values.date}`
      : 'check calendar';
  }
  if (label === 'play music') {
    return `play ${values.query || ''}`.trim();
  }
  if (label === 'set reminder') {
    return `remind me to ${values.task || ''} at ${values.time || ''}`.trim();
  }

  // Default: tool name + all non-empty values joined by spaces
  const parts = Object.values(values).filter(Boolean);
  return `${tool.display_name || humanise(tool.name)} ${parts.join(' ')}`.trim();
}

export default function ToolForm({ tool, onSubmit, onCancel }) {
  // Initialise one controlled field per input
  const initial = {};
  (tool.inputs || []).forEach(inp => { initial[inp.name] = ''; });
  const [values, setValues] = useState(initial);

  const toolLabel = tool.display_name || humanise(tool.name);

  const handleChange = (name, val) =>
    setValues(prev => ({ ...prev, [name]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(buildCommand(tool, values));
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-[#1e1e1e] border border-[#CC5500] rounded-lg p-4 mx-4 my-2"
    >
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[#CC5500] text-sm font-medium">
          <span aria-hidden="true">{getIcon(tool.icon, 16)}</span>
          {toolLabel}
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-[#666] hover:text-white transition-colors focus:outline-none"
          aria-label="Cancel"
        >
          <LucideIcons.X size={16} />
        </button>
      </div>

      {/* ── Fields ──────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3">
        {(tool.inputs || []).map(inp => (
          <div key={inp.name}>
            <label className="text-[#aaa] text-xs mb-1 block">
              {inp.label || humanise(inp.name)}
              {inp.required && (
                <span className="text-[#CC5500] ml-0.5" aria-hidden="true">*</span>
              )}
            </label>
            <input
              type={inp.type === 'date' ? 'date' : 'text'}
              value={values[inp.name] || ''}
              onChange={e => handleChange(inp.name, e.target.value)}
              placeholder={inp.placeholder || ''}
              required={!!inp.required}
              className="
                w-full bg-[#111111] border border-[#2a2a2a] text-white text-sm
                placeholder-[#555] rounded px-3 py-2
                focus:border-[#CC5500] focus:outline-none
                transition-colors duration-150
              "
            />
          </div>
        ))}
      </div>

      {/* ── Submit ──────────────────────────────────────────────────── */}
      <button
        type="submit"
        className="
          mt-4 w-full bg-[#CC5500] hover:bg-[#aa4400]
          text-white text-sm font-medium rounded px-4 py-2
          transition-colors duration-150 focus:outline-none cursor-pointer
        "
      >
        Send to Tegan
      </button>
    </form>
  );
}
